import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

function generateFingerprint() {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fingerprint', 2, 2);
  const canvasData = canvas.toDataURL();

  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
    !!window.indexedDB,
    navigator.hardwareConcurrency || 'unknown',
    navigator.deviceMemory || 'unknown',
    canvasData.slice(-50),
    navigator.platform,
    !!navigator.maxTouchPoints,
  ];

  let hash = 0;
  const str = components.join('|||');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return 'fp_' + Math.abs(hash).toString(36) + '_' + Date.now().toString(36);
}

export function useVisitorTracking() {
  const [fingerprintId, setFingerprintId] = useState(null);
  const [sessionId] = useState(() => 'sess_' + Math.random().toString(36).substr(2, 12));
  const startTime = useRef(Date.now());
  const trackedPages = useRef(new Set());

  useEffect(() => {
    let fp = localStorage.getItem('fingerprintId');
    if (!fp) {
      fp = generateFingerprint();
      localStorage.setItem('fingerprintId', fp);
    }
    setFingerprintId(fp);

    document.cookie = `fingerprintId=${fp}; max-age=${365 * 86400}; path=/; SameSite=Lax`;
    document.cookie = `sessionId=${sessionId}; max-age=${30 * 86400}; path=/; SameSite=Lax`;
    document.cookie = `screenSize=${screen.width}x${screen.height}; max-age=${365 * 86400}; path=/; SameSite=Lax`;
  }, [sessionId]);

  const trackPageView = useCallback(async (path, pageName) => {
    if (!fingerprintId || trackedPages.current.has(path)) return;
    trackedPages.current.add(path);

    try {
      await api.post('/analytics/track', {
        page: pageName || path,
        path,
        referrer: document.referrer || 'direct',
        sessionId,
        fingerprintId,
        screenSize: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        touchSupport: 'ontouchstart' in window,
        duration: 0,
      });
    } catch (err) {
      // silent
    }
  }, [fingerprintId, sessionId]);

  const trackDuration = useCallback(async (path) => {
    if (!fingerprintId) return;
    const duration = Math.round((Date.now() - startTime.current) / 1000);

    try {
      await api.post('/analytics/track', {
        page: path,
        path,
        sessionId,
        fingerprintId,
        screenSize: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        duration,
      });
    } catch (err) {
      // silent
    }
  }, [fingerprintId, sessionId]);

  const trackEvent = useCallback(async (action, details = {}) => {
    if (!fingerprintId) return;
    try {
      await api.post('/analytics/track', {
        page: action,
        path: window.location.pathname,
        sessionId,
        fingerprintId,
        screenSize: `${screen.width}x${screen.height}`,
        ...details,
      });
    } catch (err) {
      // silent
    }
  }, [fingerprintId, sessionId]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      trackDuration(window.location.pathname);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [trackDuration]);

  return { fingerprintId, sessionId, trackPageView, trackDuration, trackEvent };
}
